import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyCode,
  PaymentStatus,
  UserRole,
  formatMoney,
  type Payment,
  type PaymentStatus as PaymentStatusType,
  type UserRole as UserRoleType,
} from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { SubaccountName } from './SubaccountName';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { usePayments } from '@/data/hooks';
import { paymentLabel } from '@/lib/names';
import { PAYMENT_STATUS_TABS, paymentMethodLabel } from './labels';
import { PaymentDecisionActions } from './PaymentDecisionActions';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, sortItems, thenBy, type SortOption } from '@/lib/sort';

/** USD value used to compare amounts across currencies (a VES payment is converted with its own rate). */
const usdMinor = (p: Payment): number | undefined => {
  if (p.currency === CurrencyCode.USD) return p.amountMinor;
  if (p.currency === CurrencyCode.VES && p.exchangeRate && p.exchangeRate > 0) return Math.round(p.amountMinor / p.exchangeRate);
  return undefined;
};

const PAYMENT_SORT: SortOption<Payment>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((p) => p.createdAt, 'desc') },
  { value: 'oldest', label: 'Más antiguos', compare: byDate((p) => p.createdAt) },
  { value: 'amount_desc', label: 'Monto mayor', compare: byNumber(usdMinor, 'desc') },
  { value: 'amount_asc', label: 'Monto menor', compare: byNumber(usdMinor) },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<Payment>(
      (a, b) => statusLabel('payment', a.status).localeCompare(statusLabel('payment', b.status), 'es'),
      byDate((p) => p.createdAt, 'desc'),
    ),
  },
];

/** Roles that may approve/reject payments (mirrors backend — UX gate only). */
const REVIEW_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE,
];

interface PaymentsListPageProps {
  /** Group root for detail + upload links, e.g. "/app/customer/pagos". */
  basePath: string;
  title?: string;
  description?: string;
  /** Show the "Subir comprobante de pago" action (customer/sales/distributor). */
  showUpload?: boolean;
  /** Distributor portal: name the sub-account each payment belongs to. */
  showSubaccount?: boolean;
}

/**
 * Pagos list with status-filter tabs (Pendiente / Aprobado / Rechazado). Scoping
 * is server-side (customer/distributor → own, sales_rep → assigned, finance/admin
 * → all). For reviewer roles, PENDING rows expose an inline Aprobar/Rechazar
 * review action — this is the finance review queue. Money shown USD (+ VES when
 * the payment was made in bolívares). Responsive: table on desktop, cards on mobile.
 */
export function PaymentsListPage({
  basePath,
  title = 'Pagos',
  description = 'Pagos registrados y su estado.',
  showUpload = false,
  showSubaccount = false,
}: PaymentsListPageProps) {
  const { profile } = useSessionProfile();
  const isReviewer = profile ? REVIEW_ROLES.includes(profile.role) : false;

  // Reviewers land on the pending queue; everyone else on pending too (most relevant).
  const [status, setStatus] = useState<PaymentStatusType | undefined>(PaymentStatus.PENDING);

  const query = usePayments({ status, limit: 100 });
  const [sort, setSort] = useState('recent');
  const items = sortItems(query.data?.items ?? [], PAYMENT_SORT, sort);

  const uploadPath = `${basePath}/subir`;
  const pendingCount = useMemo(
    () => (status === PaymentStatus.PENDING ? items.length : undefined),
    [status, items.length],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={
          isReviewer && status === PaymentStatus.PENDING
            ? `Cola de revisión${pendingCount != null ? ` · ${pendingCount} pendiente${pendingCount === 1 ? '' : 's'}` : ''}.`
            : description
        }
        actions={
          showUpload ? (
            <Link to={uploadPath}>
              <Button size="sm">Subir comprobante</Button>
            </Link>
          ) : undefined
        }
      />

      {/* Status filter tabs. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="scrollbar-hover -mx-1 flex min-w-0 gap-2 overflow-x-auto px-1 pb-1">
        {PAYMENT_STATUS_TABS.map((tab) => {
          const active = tab.status === status;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setStatus(tab.status)}
              aria-pressed={active}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-brand-primary bg-brand-primary/10 font-medium text-brand-primary'
                  : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <SortSelect options={PAYMENT_SORT} value={sort} onChange={setSort} />
      </div>

      <Card className="overflow-hidden">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingLabel="Cargando pagos…"
          loadingVariant="table"
          skeletonCols={showSubaccount ? 6 : 5}
          emptyTitle="No hay pagos"
          emptyDescription={
            isReviewer && status === PaymentStatus.PENDING
              ? 'No hay pagos pendientes de revisión.'
              : 'No se encontraron pagos para este filtro.'
          }
          emptyAction={
            showUpload ? (
              <Link to={uploadPath}>
                <Button size="sm">Subir comprobante de pago</Button>
              </Link>
            ) : undefined
          }
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Pago</th>
                  {showSubaccount ? <th className="px-4 py-3 font-medium">Subcliente</th> : null}
                  <th className="px-4 py-3 font-medium">Método</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 text-right font-medium">Monto</th>
                  {isReviewer && status === PaymentStatus.PENDING ? (
                    <th className="px-4 py-3 text-right font-medium">Revisión</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <PaymentRow
                    key={p.paymentId}
                    payment={p}
                    basePath={basePath}
                    showSubaccount={showSubaccount}
                    showReview={isReviewer && p.status === PaymentStatus.PENDING}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {items.map((p) => (
              <li key={p.paymentId} className="p-4">
                <Link
                  to={{ pathname: `${basePath}/${p.paymentId}`, search: `?accountId=${encodeURIComponent(p.accountId)}` }}
                  className="flex flex-col gap-2 active:opacity-80"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-sm text-brand-primary">{paymentLabel(p)}</span>
                    <StatusPill kind="payment" value={p.status} />
                  </div>
                  {showSubaccount ? (
                    <span className="text-sm text-text-primary">
                      <SubaccountName payment={p} />
                    </span>
                  ) : null}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      {paymentMethodLabel(p.method)} · {formatDate(p.createdAt)}
                    </span>
                    <span className="font-semibold text-text-primary">{paymentAmount(p)}</span>
                  </div>
                </Link>
                {isReviewer && p.status === PaymentStatus.PENDING ? (
                  <div className="mt-3 border-t border-border pt-3">
                    <PaymentDecisionActions paymentId={p.paymentId} accountId={p.accountId} size="sm" />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

/** A desktop table row; reviewers get an expandable inline Aprobar/Rechazar panel. */
function PaymentRow({
  payment: p,
  basePath,
  showSubaccount,
  showReview,
}: {
  payment: Payment;
  basePath: string;
  showSubaccount: boolean;
  showReview: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-border last:border-0 hover:bg-surface-muted">
        <td className="px-4 py-3">
          <Link
            to={{ pathname: `${basePath}/${p.paymentId}`, search: `?accountId=${encodeURIComponent(p.accountId)}` }}
            className="font-mono text-brand-primary hover:underline"
          >
            {paymentLabel(p)}
          </Link>
        </td>
        {showSubaccount ? (
          <td className="px-4 py-3 text-text-primary">
            <SubaccountName payment={p} />
          </td>
        ) : null}
        <td className="px-4 py-3 text-text-muted">{paymentMethodLabel(p.method)}</td>
        <td className="px-4 py-3">
          <StatusPill kind="payment" value={p.status} />
        </td>
        <td className="px-4 py-3 text-text-muted">{formatDate(p.createdAt)}</td>
        <td className="px-4 py-3 text-right font-medium text-text-primary">{paymentAmount(p)}</td>
        {showReview ? (
          <td className="px-4 py-3 text-right">
            <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
              {open ? 'Cerrar' : 'Revisar'}
            </Button>
          </td>
        ) : null}
      </tr>
      {showReview && open ? (
        <tr className="border-b border-border bg-surface-muted/40">
          <td colSpan={showSubaccount ? 7 : 6} className="px-4 py-3">
            <PaymentDecisionActions paymentId={p.paymentId} accountId={p.accountId} size="sm" onDecided={() => setOpen(false)} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The payment amount label. Amounts are stored in the payment's own currency
 * (minor units). For a VES payment we also show the USD equivalent from the
 * recorded rate so reviewers see both.
 */
function paymentAmount(p: Payment): string {
  const primary = formatMoney(p.amountMinor, p.currency);
  if (p.currency === CurrencyCode.VES && p.exchangeRate && p.exchangeRate > 0) {
    const usdMinor = Math.round(p.amountMinor / p.exchangeRate);
    return `${primary} · ${formatMoney(usdMinor, CurrencyCode.USD)}`;
  }
  return primary;
}

/** Compact local date (es-VE); raw value fallback if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
